import { useState, useEffect, useCallback, useMemo } from 'react'
import styles from './styles.module.css'
import OrderNotesTable, {
  OnsiteCancellationNote,
} from '../../../global/components/order-notes-table/OrderNotesTable'
import { fetchData } from '../../../global/utils/fetch'
import formatCurrency from '../../../global/utils/format-currency-usd'
import { IOrderNote } from '../../../../../app/entities/OrderNote'
import { IOrder } from '../../../../../app/entities/Order'
import { ITechnician } from '../../../../../app/entities/Technician'
import { getOrderNotes } from '../../../work-orders/components/work-order-detail/components/notes/api'

interface OrderDetailsPanelProps {
  orderData?: IOrder
  reportData?: any
}

function formatAppointmentTime(timeSlot?: {
  fromTime?: Date | string
  toTime?: Date | string
}) {
  if (!timeSlot?.fromTime) return '-'

  const fromDate = new Date(timeSlot.fromTime)
  const dateStr = fromDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const fromTime = fromDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })

  return `${dateStr} at ${fromTime}`
}

function getVehicleDescription(orderVehicle?: any): string {
  if (!orderVehicle?.customerVehicle) return '-'

  const cv = orderVehicle.customerVehicle
  const parts: string[] = []

  if (cv.year) parts.push(cv.year.toString())
  if (cv.brand?.name) parts.push(cv.brand.name)
  if (cv.model) parts.push(cv.model)

  return parts.length > 0 ? parts.join(' ') : '-'
}

function getCustomerName(customer?: any): string {
  if (!customer) return '-'
  const parts: string[] = []
  if (customer.firstName) parts.push(customer.firstName)
  if (customer.lastName) parts.push(customer.lastName)
  return parts.length > 0 ? parts.join(' ') : '-'
}

function getTROrderNumbers(tireRackOrders?: any[]): string {
  if (!tireRackOrders || tireRackOrders.length === 0) return '-'
  return tireRackOrders.map((tro) => tro.orderNumber).filter(Boolean).join(', ') || '-'
}

function getCaseInfo(cases?: any[]): { type: string; status: string; summary: string | null; damageCost: number | null } | null {
  if (!cases || cases.length === 0) return null
  // Get the most recent case (assuming last in array or first)
  const caseItem = cases[0]
  return {
    type: caseItem.type || '-',
    status: caseItem.caseStatus || caseItem.investigationStatus || '-',
    summary: caseItem.summary || null,
    damageCost: caseItem.damageCost ?? null,
  }
}

export default function OrderDetailsPanel({
  orderData,
  reportData,
}: OrderDetailsPanelProps) {
  const [notes, setNotes] = useState<IOrderNote[]>([])
  const [isLoadingNotes, setIsLoadingNotes] = useState(false)

  // Get order object ID - prioritize reportData.order if available (for existing reports)
  const orderObjectId = reportData?.order?.objectId || orderData?.objectId

  // Merge data from both sources - reportData has loaded relations, orderData may have more
  const order = reportData?.order || orderData
  const technician: ITechnician | undefined = reportData?.technician || orderData?.lockedTechnician

  const loadNotes = useCallback(async () => {
    if (!orderObjectId) return

    setIsLoadingNotes(true)
    try {
      const fetchedNotes = await fetchData<IOrderNote[]>(getOrderNotes(orderObjectId))
      setNotes(fetchedNotes || [])
    } catch (err) {
      console.error('Error loading order notes:', err)
    } finally {
      setIsLoadingNotes(false)
    }
  }, [orderObjectId])

  useEffect(() => {
    loadNotes()
  }, [loadNotes])

  // Create legacy note from order.note field if it exists
  const legacyNoteFromOrder = useMemo((): IOrderNote | null => {
    const orderNote = order?.note
    if (!orderNote || orderNote.trim() === '') return null

    // Check if already migrated
    const alreadyMigrated = notes.some(
      (n) => n.type === 'legacy_note' && n.content === orderNote,
    )
    if (alreadyMigrated) return null

    return {
      objectId: `legacy-${order.objectId}`,
      type: 'legacy_note',
      content: orderNote,
      createdBy: null,
      created: null as unknown as number,
      updated: null as unknown as number,
    }
  }, [order?.note, order?.objectId, notes])

  // Extract on-site cancellation data
  const onsiteCancellation = useMemo((): OnsiteCancellationNote | null => {
    const orderVehicles = order?.orderVehicles || []
    const vehicleWithCos = orderVehicles.find(
      (ov: any) => ov.cosReason1 || ov.cosNote || ov.cosReason2 || ov.cosReason3,
    )

    if (!vehicleWithCos) return null

    return {
      cosReason1: vehicleWithCos.cosReason1,
      cosReason2: vehicleWithCos.cosReason2,
      cosReason3: vehicleWithCos.cosReason3,
      cosNote: vehicleWithCos.cosNote,
    }
  }, [order?.orderVehicles])

  // Combine notes
  const allNotes = useMemo(() => {
    if (legacyNoteFromOrder) {
      return [...notes, legacyNoteFromOrder]
    }
    return notes
  }, [notes, legacyNoteFromOrder])

  // Get display values
  const customerName = getCustomerName(order?.customer)
  const vehicle = getVehicleDescription(order?.orderVehicles?.[0])
  const trOrderNumbers = getTROrderNumbers(order?.tireRackOrders)
  const workOrderId = order?.workOrderId?.toString() || '-'
  const technicianName = technician?.name || '-'
  // Get timeSlot from order (loaded via reportData) or from orderData prop
  const timeSlot = order?.timeSlot || orderData?.timeSlot
  const appointmentTime = formatAppointmentTime(timeSlot)
  // Get case info from order
  const caseInfo = getCaseInfo(order?.cases)

  return (
    <div className={styles.orderDetailsPanel}>
      <div className={styles.orderDetailsPanelHeader}>
        <span className={styles.orderDetailsPanelTitle}>Work Order Details</span>
      </div>

      <div className={styles.orderDetailsPanelContent}>
        <div className={styles.orderInfoSection}>
          <div className={styles.orderInfoRow}>
            <span className={styles.orderInfoLabel}>Customer</span>
            <span className={styles.orderInfoValue}>{customerName}</span>
          </div>
          <div className={styles.orderInfoRow}>
            <span className={styles.orderInfoLabel}>Vehicle</span>
            <span className={styles.orderInfoValue}>{vehicle}</span>
          </div>
          <div className={styles.orderInfoRow}>
            <span className={styles.orderInfoLabel}>TR Order #</span>
            <span className={styles.orderInfoValue}>{trOrderNumbers}</span>
          </div>
          <div className={styles.orderInfoRow}>
            <span className={styles.orderInfoLabel}>Work Order ID</span>
            <span className={styles.orderInfoValue}>{workOrderId}</span>
          </div>
          <div className={styles.orderInfoRow}>
            <span className={styles.orderInfoLabel}>Technician</span>
            <span className={styles.orderInfoValue}>{technicianName}</span>
          </div>
          <div className={styles.orderInfoRow}>
            <span className={styles.orderInfoLabel}>Appointment</span>
            <span className={styles.orderInfoValue}>{appointmentTime}</span>
          </div>
          {caseInfo && (
            <>
              <div className={styles.orderInfoRow}>
                <span className={styles.orderInfoLabel}>Case Type</span>
                <span className={styles.orderInfoValue}>{caseInfo.type}</span>
              </div>
              <div className={styles.orderInfoRow}>
                <span className={styles.orderInfoLabel}>Case Status</span>
                <span className={styles.orderInfoValue}>{caseInfo.status}</span>
              </div>
              {caseInfo.damageCost !== null && (
                <div className={styles.orderInfoRow}>
                  <span className={styles.orderInfoLabel}>Damage Cost</span>
                  <span className={styles.orderInfoValue}>{formatCurrency(caseInfo.damageCost)}</span>
                </div>
              )}
              {caseInfo.summary && (
                <div className={styles.orderInfoRow}>
                  <span className={styles.orderInfoLabel}>Case Summary</span>
                  <span className={styles.orderInfoValue}>{caseInfo.summary}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.orderNotesSection}>
          <OrderNotesTable
            notes={allNotes}
            isLoading={isLoadingNotes}
            title="Order Notes"
            maxHeight={300}
            onsiteCancellation={onsiteCancellation}
          />
        </div>
      </div>
    </div>
  )
}
